import * as Sentry from '@sentry/react';
import type { DriverState } from '@playcastdotio/CoreTypes';
import type { DeviceConfigManager } from './DeviceConfigManager';

const DIAG = '[BLUE-STREAM-12010:ShadowcamPresenceMonitor]';

/** Matches PlayJector driver_status keys for plugged Windows slots (ShadowCam00, ShadowCam100, …). */
const SHADOWCAM_DRIVER_KEY_PATTERN = /^ShadowCam\d+$/;

/**
 * Grace window after driver plugs ahead of Chromium enumerateDevices().
 *
 * PlayJector plug + devicechange can lag driverState by hundreds of ms to a few
 * seconds (Android multi-instance plugs several slots in sequence). During this
 * window health stays provisionally healthy so matchmakeability is not yanked
 * from the pool; after the window, missing enumeration is a hard unhealthy.
 * Also gates deferred Sentry capture for the same race.
 *
 * Matches dynamic-shadowcam-pool Phase 4: unhealthy only after settle.
 */
export const LAUNCH_PRESENCE_SETTLE_MS = 5_000;

export type ShadowcamUnhealthyReason = 'shadowcam_unhealthy';

export type ShadowcamHealth = {
  healthy: boolean;
  reason: ShadowcamUnhealthyReason | null;
  enumeratedShadowcams: number;
  driverShadowcams: { name: string; present: boolean }[] | null;
};

export type ShadowcamHealthListener = (health: ShadowcamHealth) => void;

export interface ShadowcamHealthSource {
  getHealth(): ShadowcamHealth;
  onHealthChange(listener: ShadowcamHealthListener): () => void;
  setRequiredSessionCameras(cameraNames: readonly string[]): void;
}

type DriverStateLike = DriverState | Record<string, boolean> | undefined;
type GetDriverStateFn = () => DriverStateLike;

/** Plugged slots from PlayJector driver_status (`true` ShadowCamNN keys only). */
export function getPluggedShadowcamDriverKeys(driverState: DriverStateLike): string[] {
  if (!driverState) {
    return [];
  }
  const record = driverState as Record<string, boolean>;
  return Object.entries(record)
    .filter(([key, value]) => SHADOWCAM_DRIVER_KEY_PATTERN.test(key) && value === true)
    .map(([key]) => key)
    .sort();
}

/**
 * Single source of truth for shadowcam health (BLUE-STREAM-12010).
 * USB-style pool: idle (no plugged driver keys) is healthy; when plugged, every
 * driver-reported slot must appear in Chromium enumerateDevices() after settle.
 */
export class ShadowcamPresenceMonitor {
  private readonly listeners = new Set<ShadowcamHealthListener>();
  private lastNotifiedHealth: ShadowcamHealth | null = null;
  private hasReportedMissingEnumIssue = false;
  /** Active settle timer; fires hard-unhealthy + deferred Sentry when it expires. */
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Plugged-key fingerprint that armed the current settle window. */
  private settlePluggedFingerprint: string | null = null;
  /**
   * Fingerprint for which settle already expired while still mismatched.
   * Prevents re-arming an infinite provisional-healthy loop for the same set.
   */
  private settledUnhealthyFingerprint: string | null = null;
  private requiredSessionCameras = new Set<string>();

  constructor(
    private readonly deviceConfig: DeviceConfigManager,
    private readonly getDriverState: GetDriverStateFn,
    private readonly settleMs: number = LAUNCH_PRESENCE_SETTLE_MS,
  ) {}

  /**
   * Call once after MediaCoordinator's first enumeration.
   *
   * Emits an info breadcrumb with the boot snapshot. Mismatch vs plugged slots
   * arms the settle window (provisional healthy) rather than failing matchmakeability.
   */
  public reportLaunchPresence(): void {
    const health = this.computeHealth();

    Sentry.addBreadcrumb({
      category: 'BLUE-STREAM-12010',
      level: 'info',
      message: 'Shadowcam presence at launch',
      data: {
        enumeratedShadowcams: health.enumeratedShadowcams,
        driverShadowcams: health.driverShadowcams,
      },
    });

    if (this.settleTimer !== null) {
      Sentry.logger.warn(
        `${DIAG} Plugged shadowcams not visible at first enumerate — waiting ${this.settleMs}ms for devicechange to settle`,
      );
    }

    this.notifyHealth(health);
  }

  private clearSettleTimer(): void {
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
    this.settlePluggedFingerprint = null;
  }

  /**
   * Arm (or keep) a settle window for the current plugged-key set. Restart when
   * the plugged fingerprint changes so sequential multi-instance plugs each get
   * a full grace period for Chromium to catch up.
   */
  private armSettle(pluggedFingerprint: string): void {
    if (this.settlePluggedFingerprint === pluggedFingerprint && this.settleTimer !== null) {
      return;
    }
    this.clearSettleTimer();
    this.settlePluggedFingerprint = pluggedFingerprint;
    const armedFingerprint = pluggedFingerprint;
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null;
      this.settlePluggedFingerprint = null;
      const settled = this.computeRawHealth();
      if (settled.healthy) {
        this.settledUnhealthyFingerprint = null;
        this.notifyHealth(settled);
        return;
      }
      this.settledUnhealthyFingerprint = armedFingerprint;
      Sentry.logger.error(
        `${DIAG} Plugged shadowcams still missing from enumeration after settle window`,
      );
      this.captureMissingEnumerationSentry(settled);
      this.notifyHealth(settled);
    }, this.settleMs);
  }

  private captureMissingEnumerationSentry(health: ShadowcamHealth): void {
    if (this.hasReportedMissingEnumIssue) {
      return;
    }
    this.hasReportedMissingEnumIssue = true;
    const missing = (health.driverShadowcams ?? []).filter((d) => !d.present).map((d) => d.name);
    Sentry.logger.error(
      `${DIAG} driverState reports plugged shadowcams missing from enumeration: ${missing.join(', ')}`,
    );
    Sentry.captureMessage('[BLUE-STREAM-12010] Plugged shadowcams missing from host enumeration', {
      level: 'error',
      tags: {
        shadowcam_diag: 'BLUE-STREAM-12010',
        shadowcam_stage: 'settle_expired_missing_enum',
      },
      fingerprint: ['driver-status-plugged-missing-from-enum'],
      extra: {
        enumeratedShadowcams: health.enumeratedShadowcams,
        driverShadowcams: health.driverShadowcams,
        missingShadowcams: missing,
      },
    });
  }

  /** Re-evaluate after driver_status; mismatch is provisionally healthy until settle. */
  public reportDriverStatus(): void {
    this.notifyHealth(this.computeHealth());
  }

  public refreshAfterEnumeration(): void {
    this.notifyHealth(this.computeHealth());
  }

  public setRequiredSessionCameras(cameraNames: readonly string[]): void {
    this.requiredSessionCameras = new Set(cameraNames.filter((name) =>
      SHADOWCAM_DRIVER_KEY_PATTERN.test(name),
    ));
    this.refreshAfterEnumeration();
  }

  public getHealth(): ShadowcamHealth {
    return this.computeHealth();
  }

  public onHealthChange(listener: ShadowcamHealthListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public destroy(): void {
    this.clearSettleTimer();
    this.listeners.clear();
  }

  /**
   * Effective health for matchmakeability: during an active settle window after
   * plug-ahead-of-enum, report healthy so the host is not yanked from the pool.
   */
  private computeHealth(): ShadowcamHealth {
    const raw = this.computeRawHealth();
    if (raw.healthy) {
      this.clearSettleTimer();
      this.settledUnhealthyFingerprint = null;
      return raw;
    }

    const pluggedNames = getPluggedShadowcamDriverKeys(this.getDriverState());
    const fingerprint = pluggedNames.join(',');

    // Settle already expired for this plugged set — hard unhealthy (Phase 4).
    if (this.settledUnhealthyFingerprint === fingerprint) {
      return raw;
    }

    // New or still-settling mismatch — provisional healthy.
    if (this.settledUnhealthyFingerprint !== null && this.settledUnhealthyFingerprint !== fingerprint) {
      this.settledUnhealthyFingerprint = null;
    }
    this.armSettle(fingerprint);

    return {
      healthy: true,
      reason: null,
      enumeratedShadowcams: raw.enumeratedShadowcams,
      driverShadowcams: raw.driverShadowcams,
    };
  }

  /** Instantaneous plug ⊆ enum check with no settle grace. */
  private computeRawHealth(): ShadowcamHealth {
    const enumerated = this.deviceConfig.getShadowcams();
    const driverShadowcams = this.collectDriverShadowcams();
    const pluggedNames = getPluggedShadowcamDriverKeys(this.getDriverState());

    // Health gate: only cameras the DRIVER reports as plugged are hard
    // requirements. requiredSessionCameras are routing identifiers from the
    // instance allocator — they don't imply the hardware exists yet (the
    // Android path allocates ShadowCamNN names before the driver advertises
    // the slots, so gating on them would permanently block matchmakeability
    // until the driver state catches up).
    if (pluggedNames.length === 0) {
      return {
        healthy: true,
        reason: null,
        enumeratedShadowcams: enumerated.length,
        driverShadowcams,
      };
    }

    const enumeratedHealthy = pluggedNames.every((name) =>
      enumerated.some((device) => device.displayName.includes(name)),
    );

    return {
      healthy: enumeratedHealthy,
      reason: enumeratedHealthy ? null : 'shadowcam_unhealthy',
      enumeratedShadowcams: enumerated.length,
      driverShadowcams,
    };
  }

  private collectDriverShadowcams(): { name: string; present: boolean }[] | null {
    const drivers = this.getDriverState();
    if (!drivers || Object.keys(drivers).length === 0) {
      return null;
    }

    const pluggedNames = getPluggedShadowcamDriverKeys(drivers);
    if (pluggedNames.length === 0) {
      return [];
    }

    const enumerated = this.deviceConfig.getShadowcams();
    return pluggedNames.map((name) => ({
      name,
      present: enumerated.some((device) => device.displayName.includes(name)),
    }));
  }

  private notifyHealth(health: ShadowcamHealth): void {
    if (this.healthEquals(this.lastNotifiedHealth, health)) {
      return;
    }
    this.lastNotifiedHealth = health;
    for (const listener of this.listeners) {
      try {
        listener(health);
      } catch (err) {
        Sentry.logger.error(`${DIAG} health listener threw: ${err}`);
      }
    }
  }

  private healthEquals(a: ShadowcamHealth | null, b: ShadowcamHealth): boolean {
    if (!a) return false;
    return (
      a.healthy === b.healthy &&
      a.reason === b.reason &&
      a.enumeratedShadowcams === b.enumeratedShadowcams &&
      JSON.stringify(a.driverShadowcams) === JSON.stringify(b.driverShadowcams)
    );
  }
}
